import { LDMultiKindContext, useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';

export type FeatureFlagUserContext = {
  tenantId: string;
  userId: string;
  displayName: string;
  securityVersion: string;
};

export type FeatureFlagUserDetail = {
  nickname: string;
  name: string;
  email: string;
  email_verified: string;
};

export function useFeatureFlagContext() {
  const ldClient = useLDClient();
  const [context, setContext] = useState<LDMultiKindContext>();
  const prevContextRef = useRef<LDMultiKindContext | undefined>(undefined);
  const isContextSet = context !== undefined;
  const flags = useFlags();
  const isFlagsLoaded = useMemo(() => Object.keys(flags).length > 0, [flags]);

  useEffect(() => {
    const prevContext = prevContextRef.current;
    const identifyUser = () => {
      if (context) {
        ldClient?.identify(context);
      } else if (!context && prevContext) {
        // This is a safety net in case the context is lost after it's set the first time.
        // This will throw and escalate to DD if the context is lost.
        ldClient?.identify({ kind: 'multi', user: {}, account: {} });
      }
    };

    prevContextRef.current = context;
    identifyUser();
  }, [context, ldClient]);

  const clearUserContext = useCallback(() => {
    setContext(undefined);
  }, []);

  const setUserContext = useCallback((userAuth: FeatureFlagUserContext, userDetail: FeatureFlagUserDetail) => {
    const { tenantId, displayName: name, userId } = userAuth ?? {};
    const { email } = userDetail ?? {};
    const context: LDMultiKindContext = {
      kind: 'multi',
      user: {
        email,
        name,
        key: userId,
      },
      // TODO: Add tenant name to token and use here for name.
      // https://dev.azure.com/FirstAmCorp/FluentTPS/_workitems/edit/1791788
      account: {
        key: tenantId,
        name: tenantId,
      },
    };

    setContext((ctx) => {
      if (isEqual(ctx, context)) {
        return ctx;
      }
      return context;
    });
  }, []);

  return { setUserContext, clearUserContext, isContextSet, isFlagsLoaded };
}
